<template>
  <li
    :class="{
      'notification-snippet': true,
      'notification-snippet--rounded': isStandalone,
      'notification-snippet--without-border-bottom':
        (notification.status !== 1 && !isStandalone && isLast) || isStandalone,
      'notification-snippet--with-shadow': isStandalone,
      'notification-snippet--margin-bottom': isStandalone,
      'notification-snippet--not-viewed': notification.status !== 1 && !isStandalone,
    }"
  >
    <div class="notification-snippet__inner-wrap">
      <div
        :class="{
          'notification-snippet__left-color-line': true,
          'notification-snippet__left-color-line--hidden': notification.status === 1,
          'notification-snippet__left-color-line--wide': isStandalone,
          'notification-snippet__left-color-line--blue': notification.type === 0 || notification.type === 1,
          'notification-snippet__left-color-line--yellow': notification.type === 2,
          'notification-snippet__left-color-line--green': notification.type === 3,
          'notification-snippet__left-color-line--red': notification.type === 4,
        }"
      ></div>

      <icon-button
        :class="{
          'notification-snippet__close-button': true,
          'notification-snippet__close-button--gray': isStandalone,
          'notification-snippet__close-button--higher': isStandalone,
        }"
        color="red"
        :icon="icons.singles.icoCrossIcon"
        size="tiny"
        @click="$emit('dismiss')"
      />

      <div
        :class="{
          'notification-snippet__icon-wrapper': true,
          'notification-snippet__icon-wrapper--with-top-indent': !isStandalone,
          'notification-snippet__icon-wrapper--small': isStandalone,
        }"
      >
        <div
          v-if="notification.type === 0"
          :class="{
            'notification-snippet__icon': true,
            'notification-snippet__icon--blue': true,
          }"
          v-html="icons.singles.infoIcon"
        ></div>

        <!-- Spinner icon -->
        <div
          v-if="notification.type === 1"
          :class="{
            'spinner-icon': true,
            'notification-snippet__icon': true,
          }"
        ></div>
        <!-- / Spinner icon -->

        <div
          v-if="notification.type === 2"
          :class="{
            'notification-snippet__icon': true,
            'notification-snippet__icon--yellow': true,
          }"
          v-html="icons.singles.warningIcon"
        ></div>

        <div
          v-if="notification.type === 3"
          :class="{
            'notification-snippet__icon': true,
            'notification-snippet__icon--green': true,
          }"
          v-html="icons.singles.checkMarkCircleIcon"
        ></div>

        <div
          v-if="notification.type === 4"
          :class="{
            'notification-snippet__icon': true,
            'notification-snippet__icon--red': true,
          }"
          v-html="icons.singles.errorIcon"
        ></div>
      </div>

      <div class="notification-snippet__title-n-text">
        <div
          :class="{
            'notification-snippet__title': true,
            'notification-snippet__title--blue': isStandalone && (notification.type === 0 || notification.type === 1),
            'notification-snippet__title--yellow': isStandalone && notification.type === 2,
            'notification-snippet__title--green': isStandalone && notification.type === 3,
            'notification-snippet__title--red': isStandalone && notification.type === 4,
          }"
        >
          {{ notification.header }}
        </div>

        <div
          :class="{
            'notification-snippet__text': true,
            'notification-snippet__text--moved-to-left': isStandalone,
          }"
          v-text="notification.message"
        />

        <!-- Progress bar for loading notifications -->
        <transition name="fall-from-top-with-fade">
          <div
            v-if="notification.type === 1 && notification.progress !== undefined"
            :class="{
              'notification-snippet__progress-wrapper': true,
              'notification-snippet__progress-wrapper--moved-to-left': isStandalone,
            }"
          >
            <progress-bar :progress="notification.progress" />
          </div>
        </transition>

        <div
          v-if="notification.actions.length > 0"
          :class="{
            'notification-snippet__actions-wrapper': true,
            'notification-snippet__actions-wrapper--moved-to-left': isStandalone,
          }"
        >
          <div v-for="action in notification.actions" :key="action.id" class="notification-snippet__single-action">
            <fs-button
              v-if="action.type === NotificationActionType.Button"
              class="notification-snippet__action-button"
              secondary
              size="extra-small"
            >
              {{ action.text }}
            </fs-button>

            <a v-if="action.type === NotificationActionType.DownloadLink" :href="`${blobUrl}/${action.link}`" download>
              <fs-button class="notification-snippet__action-button" secondary size="extra-small" tag="a">
                {{ action.text }}
                <fs-button-icon
                  class="notification-snippet__action-button-icon"
                  :small="true"
                  :icon="icons.singles.arrowRightIcon"
                />
              </fs-button>
            </a>

            <a v-if="action.type === NotificationActionType.DownloadLinkApiEndpoint" :href="`/${action.link}`" download>
              <fs-button class="notification-snippet__action-button" secondary size="extra-small" tag="a">
                {{ action.text }}
                <fs-button-icon
                  class="notification-snippet__action-button-icon"
                  :small="true"
                  :icon="icons.singles.arrowRightIcon"
                />
              </fs-button>
            </a>

            <a v-if="action.type === NotificationActionType.NewTabLink" :href="action.link" target="_blank">
              <fs-button class="notification-snippet__action-button" secondary size="extra-small" tag="a">
                {{ action.text }}
                <fs-button-icon
                  class="notification-snippet__action-button-icon"
                  :small="true"
                  :icon="icons.singles.arrowRightIcon"
                />
              </fs-button>
            </a>
          </div>
        </div>
      </div>

      <div v-if="!isStandalone" class="notification-snippet__date">
        {{ createdAtHumanReadable }}
      </div>
    </div>
  </li>
</template>

<script>
import { NotificationActionType } from '@/ApiClient';
import { blobUrl } from '@lib/api';
import { parseISO, formatDistanceToNow } from 'date-fns';
import { removeTimezone } from '@lib/formatters';

// Components
import FsButton from '@components/buttons/fs-button.vue';
import FsButtonIcon from '@components/fs-button-icon.vue';
import IconButton from '@components/icon-button.vue';
import ProgressBar from '@components/progress-bar.vue';

// Icons
// singles
import arrowRightIcon from '@icons/other/arrow-right.svg';
import checkMarkCircleIcon from '@icons/other/check-mark-circle-icon.svg';
import errorIcon from '@icons/other2/error.svg';
import icoCrossIcon from '@icons/other/ico-cross.svg';
import infoIcon from '@icons/other2/info.svg';
import warningIcon from '@icons/other2/warning.svg';

export default {
  name: 'notification-item',

  props: {
    notification: { type: Object, required: true },
    isStandalone: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
  },

  components: {
    FsButton,
    FsButtonIcon,
    IconButton,
    ProgressBar,
  },

  data() {
    return {
      blobUrl,

      icons: {
        singles: {
          arrowRightIcon,
          checkMarkCircleIcon,
          errorIcon,
          icoCrossIcon,
          infoIcon,
          warningIcon,
        },
      },
    };
  },

  computed: {
    NotificationActionType() {
      return NotificationActionType;
    },
    createdAtHumanReadable() {
      return this.dateToHuman(this.notification.createdAt);
    },
  },

  methods: {
    // TODO: REFACTOR into formatters?
    dateToHuman(rawDateString) {
      const humanDate = formatDistanceToNow(removeTimezone(parseISO(new Date(rawDateString).toISOString()))) + ' ago';
      return humanDate.replace(/about\s/i, '');
    },
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

// Spinner icon ---------------------------------------------------------------
.spinner-icon {
  width: 2.7rem;
  height: 2.7rem;
  border: 3px #bcdbef solid;
  border-left-color: #77b9e5;
  border-radius: 50%;
  animation-name: spinning2;
  animation-duration: 1s;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  transform: translateZ(0);
}

@keyframes spinning2 {
  0% {
    transform: rotate(0deg) translateZ(0);
  }
  100% {
    transform: rotate(360deg) translateZ(0);
  }
}

// Notification snippet -------------------------------------------------------
.notification-snippet {
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  z-index: 0;
  display: flex;

  &__inner-wrap {
    padding: 1.8rem 5.5rem 0.7rem 3.2rem;
    display: flex;
  }

  &--margin-bottom {
    margin-bottom: 1rem;
  }

  &--with-shadow {
    box-shadow: 0 1.5rem 3rem -1rem rgba(0, 0, 0, 0.15);
  }

  &--rounded {
    border-radius: 1rem;
  }

  &::before {
    content: '';
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-bottom: 1px rgba($c-dark, 0.1) solid;
    position: absolute;
    inset: auto auto 0 0;
    z-index: -1;
    background: #fff;
  }

  &--without-border-bottom {
    &::before {
      border-bottom-width: 0;
    }
  }

  &--not-viewed {
    &::before {
      background: rgb(247, 247, 247);
    }
  }

  &__left-color-line {
    width: 0.8rem;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 2;

    &--hidden {
      opacity: 0;
    }

    &--wide {
      width: 1rem;
    }

    &--blue {
      background: $c-info;
    }
    &--red {
      background: $c-error;
    }
    &--green {
      background: $c-success;
    }
    &--yellow {
      background: $c-warning;
    }
  }

  &__date {
    position: absolute;
    inset: 2.2rem 5rem auto auto;
    font-size: 1rem;
    color: rgba($c-dark, 0.5);
    text-transform: uppercase;
  }

  &__close-button {
    position: absolute;
    inset: 2.2rem 2rem auto auto;
    transition: opacity 0.12s ease;
    transform: scale(0.9);

    &--higher {
      inset: 1.5rem 1.3rem auto auto !important;
    }

    &--gray {
      opacity: 0.5;
      transition: opacity 0.15s ease;

      &:hover {
        opacity: 1;
      }
    }
  }

  &__icon-wrapper {
    width: 2.7rem;
    min-width: 2.7rem;
    height: 2.7rem;
    margin-right: 1.8rem;

    &--with-top-indent {
      margin-top: 0.9rem;
    }

    &--small {
      margin-right: 0.7rem;
      transform: scale(0.75);
      transform-origin: top left;
    }
  }

  &__icon {
    width: 2.7rem;
    min-width: 2.7rem;
    height: 2.7rem;

    &--blue {
      :deep(svg) {
        fill: $c-info;
      }
    }
    &--red {
      :deep(svg) {
        fill: $c-error;
      }
    }
    &--green {
      :deep(svg) {
        fill: #30bb53;
      }
    }
    &--yellow {
      :deep(svg) {
        fill: #eea339;
      }
    }
  }

  &__title-n-text {
    flex-grow: 1;
  }

  &__title {
    margin-bottom: 0.8rem;

    &--blue {
      color: $c-info;
    }
    &--red {
      color: $c-error;
    }
    &--green {
      color: #30bb53;
    }
    &--yellow {
      color: #eea339;
    }
  }

  &__text {
    margin-bottom: 1.3rem;
    font-size: 1.4rem;
    line-height: 1.9rem;
    color: rgba($c-dark, 0.5);
    white-space: pre-wrap;

    &--moved-to-left {
      margin-left: -3.5rem;
    }
  }

  &__progress-wrapper {
    margin-bottom: 1.3rem;

    &--moved-to-left {
      margin-left: -3.5rem;
    }
  }

  &__actions-wrapper {
    display: flex;
    margin-bottom: 1.3rem;

    &--moved-to-left {
      margin-left: -3.5rem;
    }
  }

  &__single-action {
    &:not(:last-child) {
      margin-right: 1rem;
    }
  }

  &__action-button {
  }

  &__action-button-icon {
    margin-right: 0;
    margin-left: 0.7rem;
    position: relative;
    top: 0;
  }
}
</style>
